import React, {FC, ReactNode} from 'react';

import {BlueLight, Icon, Insurance, Sunglasses, SuperHydrophobic} from '@optimaxdev/icons';
import classNames from 'classnames';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {Price} from 'components/price/price';

import * as UvIcon from './icon/uv.svg';

import s from './upsellContent.scss';

export type PropsType = {
    childrenContent: ReactNode;
    price: number;
    name: string;
    specialPrice: number;
    needToShowPrice: boolean;
    title: string;
    popup: ReactNode;
    showPopup: () => void;
    titleStylesForDescription?: boolean;
    icon?: string;
};

const ICONS: {[key: string]: any} = {
    digital_block: BlueLight,
    hydrophobic: SuperHydrophobic,
    insurance_20_20: Insurance,
    premium_coatings: UvIcon,
    sunglasses: Sunglasses,
};

/**
 * Displays an icon for upsell
 * @param {string} icon - from backend
 * @param {string} title - of the upsell
 * @param {string} name - name of the upsell (not for display)
 * @constructor
 */
const UpsellIcon: FC<Pick<PropsType, 'icon' | 'title' | 'name'>> = ({icon, name, title}) => {
    if (ICONS[name])
        return (
            <div className={s.icon}>
                <Icon icon={ICONS[name]} />
            </div>
        );

    if (icon)
        return (
            <div className={s.icon}>
                <CloudinaryImage alt={title} src={icon} />
            </div>
        );
    return null;
};

/**
 * Returns upsell content
 * @param {PropsType} props - Props of component
 * @returns {React.ReactElement} component
 */
export const UpsellContent: React.FC<PropsType> = ({
    childrenContent,
    icon,
    name,
    needToShowPrice,
    popup,
    price,
    showPopup,
    specialPrice,
    title,
    titleStylesForDescription,
}) => {
    const renderPrice = () => {
        return (
            <span
                className={s.price}
                data-test-name="upsellPrice"
                data-test-value={specialPrice === 0 ? price : specialPrice}
            >
                (+
                {!specialPrice || specialPrice === price ? (
                    <Price>{price}</Price>
                ) : (
                    <>
                        <Price>{specialPrice}</Price>
                        <span className={s.old}>
                            <Price>{price}</Price>
                        </span>
                    </>
                )}
                )
            </span>
        );
    };

    return (
        <div className={s.contentWrap}>
            <UpsellIcon icon={icon} name={name} title={title} />
            <div>
                <div className={s.title}>
                    <span>{title}</span>
                    {needToShowPrice && renderPrice()}
                </div>
                <div
                    className={classNames(s.description, {
                        [s.title]: titleStylesForDescription,
                    })}
                >
                    <>
                        {childrenContent}
                        {popup && (
                            <button
                                className={s.link}
                                data-test-name="learnMore"
                                onClick={showPopup}
                                type="button"
                            >
                                Learn more
                            </button>
                        )}
                    </>
                </div>
            </div>
        </div>
    );
};
